import {
  arePreferencesUnavailable,
  isAcceptedPreferenceText,
  isPreferencesForbidden,
  isTransientPreferencesError,
  putConversationPreferences,
  TOOLS_MAX_LENGTH,
  type ConversationPreferences,
  type ConversationPreferencesPatch,
  type PreferenceSource,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import type { OnboardingProgress } from 'state/store/onboarding/use-onboarding-store';

/** A write that failed for a passing reason waits here and goes out again with the next write,
 *  under its newer values. */
let pendingWrite: { userId: string; patch: ConversationPreferencesPatch } | null = null;
/** Writes run one at a time in the order the answers were given, so a slow older write can
 *  never land after, or be merged over, a newer one. */
let writeQueue: Promise<void> = Promise.resolve();
/** A hung request gives up after this, so it can't hold back the later writes (completion too). */
export const ONBOARDING_WRITE_TIMEOUT_MS = 10_000;

/** Bumped by every logout: writes queued before it are dropped, never sent for the next user. */
let session = 0;
let watchingLogout = false;
const watchLogout = () => {
  if (watchingLogout) return;
  watchingLogout = true;
  useAuthStore.subscribe((state, previous) => {
    if (previous.isAuthenticated && !state.isAuthenticated) {
      session += 1;
      pendingWrite = null;
    }
  });
};

type QueuedWrite = { userId?: string; session: number; patch: ConversationPreferencesPatch };

async function sendOnboardingPreferences({ userId, session: queuedIn, patch }: QueuedWrite) {
  // The request goes out as whoever is signed in now, so it must still be the user who answered.
  if (!userId || queuedIn !== session || useCommonStore.getState().user?.id !== userId) return;
  const carried = pendingWrite?.userId === userId ? pendingWrite?.patch : undefined;
  pendingWrite = null;
  if (arePreferencesUnavailable(userId)) return;

  // Each salutation and industry patch carries its text too, so field by field the newer wins.
  const merged = { ...carried, ...patch };
  try {
    await putWithTimeout(merged, 'onboarding');
  } catch (error) {
    if (isPreferencesForbidden(error)) return;
    // A rejected value would fail again and take every later answer with it; a timeout or a
    // network error may pass, so that write goes out again with the next one.
    if (isTransientPreferencesError(error)) pendingWrite = { userId, patch: merged };
    console.error('Failed to save onboarding preferences:', error);
  }
}

/** One PUT that gives up (and aborts) after the timeout, so a hung request can't hold the queue. */
async function putWithTimeout(patch: ConversationPreferencesPatch, source: PreferenceSource) {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error('Saving conversation preferences timed out'));
    }, ONBOARDING_WRITE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      putConversationPreferences(patch, source, controller.signal),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Saves onboarding answers to the shared preference, so every agent uses them. Fire-and-forget:
 * it never blocks the next question and never throws. A refused session (403) writes nothing.
 * Resolves once this write (and every earlier one) is done.
 */
export function saveOnboardingPreferences(patch: ConversationPreferencesPatch) {
  watchLogout();
  const write = { userId: useCommonStore.getState().user?.id, session, patch };
  writeQueue = writeQueue.then(() => sendOnboardingPreferences(write));
  return writeQueue;
}

/**
 * Saves a change from the settings card in the same queue as the onboarding answers, so an
 * older onboarding write still waiting can never land after it, and nothing goes out for
 * another account after a logout. Unlike the onboarding writes it reports failure (it rejects)
 * and is never carried forward. Once saved, an onboarding write still carried from an earlier
 * failure no longer brings back the fields saved here.
 */
export function saveSettingsPreferences(
  patch: ConversationPreferencesPatch
): Promise<ConversationPreferences> {
  watchLogout();
  const userId = useCommonStore.getState().user?.id;
  const queuedIn = session;
  const write = writeQueue.then(async () => {
    if (!userId || queuedIn !== session || useCommonStore.getState().user?.id !== userId) {
      throw new Error('The user changed before the preferences were saved');
    }
    const saved = await putWithTimeout(patch, 'settings');
    if (pendingWrite?.userId === userId) {
      const carried = { ...pendingWrite.patch };
      (Object.keys(patch) as (keyof ConversationPreferencesPatch)[]).forEach((field) => {
        delete carried[field];
      });
      pendingWrite = Object.keys(carried).length ? { userId, patch: carried } : null;
    }
    return saved;
  });
  writeQueue = write.then(
    () => undefined,
    () => undefined
  );
  return write;
}

/** The name step's fields. English has a single form, so it sends no form or salutation. */
export function nameStepPreferences(progress: OnboardingProgress): ConversationPreferencesPatch {
  const names = {
    givenName: progress.givenName ?? progress.name ?? null,
    familyName: progress.familyName ?? null,
  };
  if (!progress.addressForm) return names;
  return { addressForm: progress.addressForm, ...names, ...salutationPreferences(progress) };
}

export const salutationPreferences = (
  progress: Pick<OnboardingProgress, 'salutation' | 'salutationText'>
): ConversationPreferencesPatch => ({
  salutation: progress.salutation ?? null,
  salutationText: progress.salutation === 'custom' ? (progress.salutationText ?? null) : null,
});

export const industryPreferences = (
  progress: Pick<OnboardingProgress, 'industry' | 'industryText'>
): ConversationPreferencesPatch => ({
  industry: progress.industry ?? null,
  industryText: progress.industry === 'other' ? (progress.industryText ?? null) : null,
});

/** Field groups that travel together: a group is left out when the server holds any of its
 *  stored fields. The two names are one group, so a re-send never pairs a name saved elsewhere
 *  with the other name from old onboarding answers. */
const SAVED_ELSEWHERE: [(keyof ConversationPreferences)[], (keyof ConversationPreferencesPatch)[]][] =
  [
    [['addressForm'], ['addressForm']],
    [['salutation'], ['salutation', 'salutationText']],
    [
      ['givenName', 'familyName'],
      ['givenName', 'familyName'],
    ],
    [['industry'], ['industry', 'industryText']],
    [['workArea'], ['workArea']],
    [['tools'], ['tools']],
  ];

/** Drops every answer the stored preference already holds (from settings, the Klax app or an
 *  earlier write), so a late re-send of old onboarding answers only fills gaps. */
export function onlyMissingPreferences(
  patch: ConversationPreferencesPatch,
  stored: ConversationPreferences
): ConversationPreferencesPatch {
  const missing = { ...patch };
  SAVED_ELSEWHERE.forEach(([storedFields, fields]) => {
    const isHeld = storedFields.some((field) => {
      const value = stored[field];
      return value != null && value !== 'unknown';
    });
    if (isHeld) fields.forEach((field) => delete missing[field]);
  });
  return missing;
}

/** The answers a completion sends for this progress, without the done flag. */
export function onboardingCompletionAnswers(
  progress: OnboardingProgress
): ConversationPreferencesPatch {
  let answers: ConversationPreferencesPatch;
  if (progress.catchUp) {
    answers = progress.addressForm
      ? {
          addressForm: progress.addressForm,
          ...(progress.salutation ? salutationPreferences(progress) : {}),
        }
      : {};
  } else {
    answers = {
      ...(progress.givenName || progress.name ? nameStepPreferences(progress) : {}),
      ...(progress.team?.trim() ? { workArea: progress.team.trim() } : {}),
      ...(isAcceptedPreferenceText(progress.tools, TOOLS_MAX_LENGTH)
        ? { tools: progress.tools.trim() }
        : {}),
    };
  }
  return { ...answers, ...(progress.industry ? industryPreferences(progress) : {}) };
}

/**
 * What the preference keeps beyond the answers, so another device can resume (the local store
 * stays what this app reads): the step reached, the tools answer, the guessed-salutation hint
 * and the catch-up skips. Only what changed is sent. A catch-up writes nothing until it
 * completes, so [Später] never touches the answers or the step of a finished old onboarding.
 */
export function progressPreferences(
  previous: OnboardingProgress | null,
  next: OnboardingProgress
): ConversationPreferencesPatch {
  if (next.catchUp && !next.completed) return {};
  const patch: ConversationPreferencesPatch = {};
  if (next.finishedStep !== undefined && next.finishedStep !== previous?.finishedStep) {
    patch.onboardingStep = Math.min(next.finishedStep, 4);
  }
  if (Boolean(next.salutationInferred) !== Boolean(previous?.salutationInferred)) {
    patch.salutationInferred = Boolean(next.salutationInferred);
  }
  if (next.catchUpSkips !== undefined && next.catchUpSkips !== previous?.catchUpSkips) {
    patch.catchUpSkips = next.catchUpSkips;
  }
  const tools = next.tools?.trim() ?? '';
  if (tools !== (previous?.tools?.trim() ?? '')) {
    // Cleared when the user goes back to change an earlier answer.
    if (!tools) patch.tools = null;
    else if (isAcceptedPreferenceText(tools, TOOLS_MAX_LENGTH)) patch.tools = tools;
  }
  return patch;
}

/** Every collected answer plus the done flag. Sent at completion, so answers whose own write
 *  failed reach the preference too. The catch-up only sends what it asked, so it never
 *  overwrites names saved elsewhere with the old onboarding's single name. A re-send at a later
 *  login passes the stored preference (`keepStored`): the local answers may be older than what
 *  was saved since (e.g. in settings), so they only fill what the server lacks. */
export function completeOnboardingPreferences(
  progress: OnboardingProgress,
  keepStored?: ConversationPreferences
) {
  const patch = onboardingCompletionAnswers(progress);
  return saveOnboardingPreferences({
    ...(keepStored ? onlyMissingPreferences(patch, keepStored) : patch),
    onboardingVersion: 2,
  });
}
